import { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { jwtDecode } from 'jwt-decode';

import {
  NOT_AVAILABLE,
  SAM_ROLE_STRATEGY_ADMIN,
  SRO_ROLE_COMM_MGR,
  SRO_ROLE_SBM
} from './AppConstants';
import { useLoginState } from './LoginStateContext';
import {
  fetchJsonWithAuth,
  setAuthorizationToken,
  setLoggedInUserId,
  setLogoutFunction
} from './fetch-json.util';

import { recordSAMUserActivity } from './APIHelper';
import { getEnvVar } from '../utils/getEnvVar';

export function useUserLogin() {
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [userSessionObj, setUserSessionObj] = useState(null);
  const [, setLoginState] = useLoginState();

  const logout = async () => {
    oktaAuth.stop();
    // Redirect to '/' after logout
    clearSessionInfo();
    await oktaAuth.signOut({
      postLogoutRedirectUri: 'https://jdsn.deere.com'
    });
  };

  function getAdjustedUserRoles(rolesObj) {
    return rolesObj && rolesObj.length > 0 ? rolesObj : [];
  }

  function hasViewer(rolesObj) {
    return (
      rolesObj && rolesObj.length > 0 && rolesObj.includes('STRATEGY_VIEWER')
    );
  }

  function setUserObjInSession(info, accessToken, rolesObj, msg) {
    let adjustedRoles = getAdjustedUserRoles(rolesObj);
    let isViewer = hasViewer(rolesObj);
    let isOwner = rolesObj ? rolesObj.includes('STRATEGY_OWNER') : false;
    let isAdmin = rolesObj ? rolesObj.includes(SAM_ROLE_STRATEGY_ADMIN) : false;
    let isSBM = rolesObj ? rolesObj.includes(SRO_ROLE_SBM) : false;
    let isCommMgr = rolesObj ? rolesObj.includes(SRO_ROLE_COMM_MGR) : false;

    let userId = info.userID;
    let userName = getFormattedUserNameInt(
      info.lastName,
      info.firstName,
      info.middleInitial
    );

    let userObj = {
      userId,
      userName,
      defaultUser: {
        value: userId.toUpperCase(),
        label: `${userName} -  ${userId.toUpperCase()}`
      },
      token: accessToken,
      roles: adjustedRoles,
      isViewer: isViewer,
      isOwner: isOwner,
      isAdmin: isAdmin,
      isSBM: isSBM,
      isCommMgr: isCommMgr,
      hasAccess: isViewer || isOwner || isAdmin || isSBM || isCommMgr,
      hasCreateAccess: isAdmin || isSBM || isCommMgr,
      message: msg
    };
    // Set Global Login State for the header component
    setLoginState(userObj);
    setSessionInfo(userObj);
    setUserSessionObj(userObj);
  }

  async function handleScopeCheck(token) {
    const decoded = jwtDecode(token);
    const tokenScopes = decoded?.scp || [];
    const configuredScopes = oktaAuth?.options?.scopes;
    const requiredScopes = Array.isArray(configuredScopes)
      ? configuredScopes.map((s) => s.trim()).filter(Boolean)
      : [];
    const SCOPE_RETRY_FLAG = 'scopeReauthAttempted';
    const missingScopes = requiredScopes.filter(
      (s) => !tokenScopes.includes(s)
    );

    if (missingScopes.length > 0) {
      const isRetry = sessionStorage.getItem(SCOPE_RETRY_FLAG);

      if (isRetry) {
        console.error(
          'Required scopes are still missing after re-authentication attempt. ' +
            'Missing scopes:',
          missingScopes
        );
        sessionStorage.removeItem(SCOPE_RETRY_FLAG);
      } else {
        // First attempt: set the flag and reload once to pick up
        // a freshly minted token that may include the required scopes.
        console.log(
          'Missing required scopes, re-authenticating to fetch updated token:',
          missingScopes
        );
        sessionStorage.setItem(SCOPE_RETRY_FLAG, 'true');
      }

      clearSessionInfo();
      await oktaAuth.tokenManager.clear();

      if (isRetry) {
        await logout();
      } else {
        window.location.reload();
      }
      return true; // signals that a redirect/reload is in progress
    }

    // Scopes are present — clear any leftover retry flag from a previous session.
    sessionStorage.removeItem(SCOPE_RETRY_FLAG);
    return false;
  }

  useEffect(() => {
    const getUser = async () => {
      try {
        await oktaAuth.start();
        const accessToken = oktaAuth.getAccessToken();
        // Token scope check — triggers redirect-based re-authentication if required scopes are missing;
        const redirecting = await handleScopeCheck(accessToken);
        if (redirecting) return;

        setAuthorizationToken(accessToken);
        setLogoutFunction(logout);

        const info = await oktaAuth.getUser();

        setUserInfo(info);
        setLoggedInUserId(info.userID);

        // Guard: if a valid session with the same token already exists,
        // restore it directly without making role-fetch or SAM-activity calls.
        const existingSession = getSessionInfo();
        if (existingSession && existingSession.token === accessToken) {
          setLoginState(existingSession);
          setUserSessionObj(existingSession);
          return;
        }

        getUserRoles(info.userID)
          .then((rolesObj) => {
            setUserObjInSession(info, accessToken, rolesObj);
            // call App Usage stats (only on a fresh login, guarded above)
            if (
              rolesObj &&
              (rolesObj.includes(SAM_ROLE_STRATEGY_ADMIN) ||
                rolesObj.includes('STRATEGY_VIEWER'))
            )
              recordSAMUserActivity(info.userID.toUpperCase(), 'DMSM');
          })
          .catch(() => {
            setUserObjInSession(
              info,
              authState,
              null,
              'Role information inaccessible; please try again later'
            );
          });
      } catch (error) {
        console.log(error);
      }
    };

    authState?.isAuthenticated && getUser();
  }, [authState, oktaAuth]);

  async function getUserRoles(userId) {
    let postResp = await fetchJsonWithAuth(
      getEnvVar('VITE_USER_ROLE_API_URL') + '/roles/' + userId
    );

    return postResp.json;
  }

  return [userInfo, logout, userSessionObj];
}

const setSessionInfo = (session) => {
  sessionStorage.setItem('session', JSON.stringify(session));
};
const clearSessionInfo = () => {
  sessionStorage.removeItem('session');
  sessionStorage.removeItem('searchCriteriaLabel');
  sessionStorage.removeItem('searchCriteria');
};

function getFormattedUserNameInt(lastName, firstName, middleInitial) {
  return (
    (lastName && lastName !== NOT_AVAILABLE ? lastName : '') +
    (firstName && firstName !== NOT_AVAILABLE ? ', ' + firstName : '') +
    (middleInitial && middleInitial !== NOT_AVAILABLE
      ? ' ' + middleInitial
      : '')
  );
}

export function getFormattedUserName(userInfo) {
  return userInfo
    ? getFormattedUserNameInt(
        userInfo.lastName,
        userInfo.firstName,
        userInfo.middleInitial
      )
    : '';
}

export function getFormattedUserNameWithFallback(userInfo, userId) {
  let formatted =
    userInfo &&
    getFormattedUserNameInt(
      userInfo.lastName,
      userInfo.firstName,
      userInfo.middleInitial
    );

  if (!formatted) formatted = userId?.toUpperCase() ?? '';

  return formatted;
}

export function getFormattedUserNameWithId(userInfo, userId) {
  const name = userInfo ? getFormattedUserName(userInfo) : '';
  const id = userId?.toUpperCase() ?? '';

  // Append separator and ID only when an ID exists
  if (!id) return name;

  return name ? `${name} - ${id}` : id;
}

export function getSessionInfo() {
  let obj = sessionStorage.getItem('session');

  return obj === undefined || obj === null ? null : JSON.parse(obj);
}
